import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  SelectPicker,
  Input,
  Message,
  useToaster,
  Loader,
} from 'rsuite';
import authFetch from '../../helpers/authFetch';
import { DECK_FORMAT_RULES } from '../../helpers/deckRules';
import { parseDeckList } from '../../helpers/deckParser';
import CreateCollectionModal from './CreateCollectionModal';

const STEP = { INPUT: 'input', PREVIEW: 'preview' };

const CREATE_NEW_SENTINEL = '__create_new__';

const SCRYFALL_CHUNK_SIZE = 75;

const FORMAT_OPTIONS = [
  { label: 'Auto-detect', value: 'auto' },
  { label: 'Plain text', value: 'plaintext' },
  { label: 'Moxfield', value: 'moxfield' },
  { label: 'Archidekt', value: 'archidekt' },
  { label: 'MTGO', value: 'mtgo' },
  { label: 'Arena', value: 'arena' },
  { label: 'CSV', value: 'csv' },
];

const SPACING = {
  sectionGap: 16,
  labelHeaderGap: 6,
  resolvedListMaxHeight: 280,
  resolvedItemGap: 2,
  resolvedItemPadding: '4px 8px',
  resolvedItemBorderRadius: 3,
  resolvedItemColumnGap: 12,
  resolvedQtyMinWidth: 28,
  unresolvedItemGap: 4,
  unresolvedNotMargin: '4px 0 8px',
  unresolvedListPaddingLeft: 20,
  textareaRows: 12,
};

const FONT = {
  cardRow: 13,
  note: 12,
};

const COLORS = {
  resolvedItemBg: 'rgba(255,255,255,0.04)',
  tag: '#aaa',
  unresolvedHeading: '#e74c3c',
  mutedNote: '#aaa',
  sideboardTag: '#5dade2',
  commanderTag: '#f39c12',
};

/** Build name→card map, indexing both the full DFC name and each face name. */
function buildFoundMap(cards) {
  const map = {};
  for (const card of cards) {
    map[card.name.toLowerCase()] = card;
    if (card.name.includes(' // ')) {
      for (const face of card.name.split(' // ')) {
        const key = face.trim().toLowerCase();
        if (!map[key]) map[key] = card;
      }
    }
  }
  return map;
}

/** Look up a parsed entry name in the foundMap, trying DFC front-face as fallback. */
function lookupCard(foundMap, name) {
  return (
    foundMap[name.toLowerCase()] ||
    foundMap[name.split(' // ')[0].trim().toLowerCase()] ||
    null
  );
}

const ImportDeckModal = ({ open, onClose, onSuccess, preSelectedCollectionId }) => {
  const [step, setStep] = useState(STEP.INPUT);
  const [text, setText] = useState('');
  const [format, setFormat] = useState('auto');
  const [collectionId, setCollectionId] = useState(preSelectedCollectionId || null);
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [resolved, setResolved] = useState([]);   // [{ ...parsedEntry, scryfall_id }]
  const [unresolved, setUnresolved] = useState([]); // [name string]
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDefaults, setCreateModalDefaults] = useState({});
  const fileInputRef = useRef(null);
  const previewCancelRef = useRef(false);
  const toaster = useToaster();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCollectionId(preSelectedCollectionId || null);

    // Always fetch collections so we have the name available for the import toast
    // and for commander-warning logic regardless of preSelectedCollectionId.
    setLoadingCollections(true);
    authFetch('/collections')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => { if (!cancelled) setCollections(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingCollections(false); });

    return () => { cancelled = true; };
  }, [open, preSelectedCollectionId]);

  const resetToInput = () => {
    setStep(STEP.INPUT);
    setError('');
  };

  const handleClose = () => {
    previewCancelRef.current = true;
    setStep(STEP.INPUT);
    setText('');
    setFormat('auto');
    setCollectionId(preSelectedCollectionId || null);
    setCollections([]);
    setError('');
    setResolved([]);
    setUnresolved([]);
    setShowCreateModal(false);
    onClose();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(ev.target.result || '');
    reader.readAsText(file);
    e.target.value = ''; // allow re-uploading the same file
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setText(clipboardText);
    } catch (err) {
      setError('Failed to read from clipboard. Make sure clipboard access is allowed.');
    }
  };

  const handlePreview = async () => {
    setError('');
    setPreviewing(true);
    previewCancelRef.current = false;

    try {
      const parsed = parseDeckList(text, format);

      if (parsed.length === 0) {
        setError('No card entries found in the provided text.');
        return;
      }

      // Deduplicate names for Scryfall resolution, preserving first seen setCode.
      const nameMap = new Map(); // name → first parsed entry (for setCode)
      for (const entry of parsed) {
        if (!nameMap.has(entry.name)) nameMap.set(entry.name, entry);
      }
      const uniqueNames = [...nameMap.keys()];

      // Resolve names in chunks against POST /cards/collection
      const foundMap = {};
      for (let i = 0; i < uniqueNames.length; i += SCRYFALL_CHUNK_SIZE) {
        if (previewCancelRef.current) return;

        const chunk = uniqueNames.slice(i, i + SCRYFALL_CHUNK_SIZE);
        const identifiers = chunk.map((name) => {
          const entry = nameMap.get(name);
          const lookupName = name.includes(' // ')
            ? name.split(' // ')[0].trim()
            : name;
          return entry.setCode
            ? { name: lookupName, set: entry.setCode.toLowerCase() }
            : { name: lookupName };
        });

        try {
          const res = await authFetch('/cards/collection', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifiers }),
          });
          if (res.ok) {
            const data = await res.json();
            Object.assign(foundMap, buildFoundMap(data.data || []));
          }
        } catch {
          // chunk failed — those names will appear as unresolved
        }
      }

      if (previewCancelRef.current) return;

      const resolvedEntries = [];
      const unresolvedNames = [];

      for (const entry of parsed) {
        const card = lookupCard(foundMap, entry.name);
        if (card) {
          resolvedEntries.push({ ...entry, scryfall_id: card.id });
        } else if (!unresolvedNames.includes(entry.name)) {
          unresolvedNames.push(entry.name);
        }
      }

      if (resolvedEntries.length === 0) {
        setError('None of the card names could be resolved. Check the format and try again.');
        return;
      }

      setResolved(resolvedEntries);
      setUnresolved(unresolvedNames);
      setStep(STEP.PREVIEW);
    } catch (err) {
      if (!previewCancelRef.current) {
        setError('Failed to parse deck list: ' + err.message);
      }
    } finally {
      setPreviewing(false);
    }
  };

  // Derived values used by both render and handleImport — computed once to avoid duplication.
  const selectedCollection = collections.find((c) => c.id === collectionId);
  const hasCommander = resolved.some((e) => e.isCommander);
  const commanderWarning =
    hasCommander &&
    selectedCollection &&
    !DECK_FORMAT_RULES[selectedCollection.deck_type]?.requiresCommander
      ? `This deck list contains a Commander section but ${selectedCollection.name} does not support a commander. The commander designation will be ignored.`
      : null;

  const handleImport = async () => {
    setError('');
    setImporting(true);

    const entries = resolved.map((e) => ({
      scryfall_id: e.scryfall_id,
      quantity: e.quantity,
      condition: e.condition,
      finish: e.finish,
      is_proxy: e.isProxy,
      is_commander: commanderWarning ? false : e.isCommander,
      is_sideboard: e.isSideboard,
      notes: e.notes || null,
    }));

    try {
      const res = await authFetch(`/collections/${collectionId}/entries/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Import failed. Please try again.');
        return;
      }

      const data = await res.json();
      const n = data.imported || entries.length;
      const collName = selectedCollection?.name || 'collection';

      toaster.push(
        <Message type="success" showIcon closable>
          {n} card{n !== 1 ? 's' : ''} imported into <strong>{collName}</strong>
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );

      if (onSuccess) onSuccess();
      handleClose();
    } catch {
      setError('Unable to connect to server. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleCollectionChange = (value) => {
    if (value === CREATE_NEW_SENTINEL) {
      // Detect commanders from current text to pre-set deck type in CreateCollectionModal
      let defaults = {};
      if (text.trim()) {
        try {
          const hasCmd = parseDeckList(text, format).some((e) => e.isCommander);
          if (hasCmd) defaults = { type: 'DECK', deck_type: 'COMMANDER' };
        } catch {
          // ignore parse errors — defaults stay empty
        }
      }
      setCreateModalDefaults(defaults);
      setShowCreateModal(true);
      return; // don't assign the sentinel to collectionId
    }
    setCollectionId(value);
  };

  const handleNewCollectionCreated = (created) => {
    setCollections((prev) => [created, ...prev]);
    setCollectionId(created.id);
    setShowCreateModal(false);
  };

  const collectionOptions = [
    { label: 'Create new collection…', value: CREATE_NEW_SENTINEL },
    ...collections.map((c) => ({
      label: `${c.name} (${c.type === 'DECK' ? 'Deck' : 'Trade Binder'})`,
      value: c.id,
    })),
  ];

  const previewDisabled =
    !text.trim() || (!preSelectedCollectionId && !collectionId) || previewing;

  return (
    <>
    <Modal open={open} onClose={handleClose} size="md">
      <Modal.Header>
        <Modal.Title>Import Deck List</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Message type="error" showIcon style={{ marginBottom: SPACING.sectionGap }}>
            {error}
          </Message>
        )}

        {/* ── Step 1: Input ─────────────────────────────────────── */}
        {step === STEP.INPUT && (
          <>
            {!preSelectedCollectionId && (
              <Form.Group style={{ marginBottom: SPACING.sectionGap }}>
                <Form.ControlLabel>Collection</Form.ControlLabel>
                {loadingCollections ? (
                  <Loader content="Loading collections..." />
                ) : (
                  <SelectPicker
                    data={collectionOptions}
                    value={collectionId}
                    onChange={handleCollectionChange}
                    searchable={false}
                    block
                    placeholder="Select a collection"
                  />
                )}
              </Form.Group>
            )}

            <Form.Group style={{ marginBottom: SPACING.sectionGap }}>
              <Form.ControlLabel>Format</Form.ControlLabel>
              <SelectPicker
                data={FORMAT_OPTIONS}
                value={format}
                onChange={setFormat}
                searchable={false}
                cleanable={false}
                block
              />
            </Form.Group>

            <Form.Group>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: SPACING.labelHeaderGap,
                  gap: 8,
                }}
              >
                <Form.ControlLabel style={{ marginBottom: 0 }}>Deck List</Form.ControlLabel>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    size="xs"
                    appearance="ghost"
                    onClick={handlePasteFromClipboard}
                  >
                    Paste from clipboard
                  </Button>
                  <Button
                    size="xs"
                    appearance="ghost"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload file
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
              <Input
                as="textarea"
                rows={SPACING.textareaRows}
                value={text}
                onChange={setText}
                placeholder={
                  'Paste your deck list here, or use the buttons above.\n\n' +
                  'Examples:\n  4 Lightning Bolt\n  1 Sol Ring (CMD) 236\n  2x Counterspell [CMR]'
                }
              />
            </Form.Group>
          </>
        )}

        {/* ── Step 2: Preview ───────────────────────────────────── */}
        {step === STEP.PREVIEW && (
          <>
            {commanderWarning && (
              <Message type="warning" showIcon style={{ marginBottom: SPACING.sectionGap }}>
                {commanderWarning}
              </Message>
            )}

            <div style={{ marginBottom: SPACING.sectionGap }}>
              <strong>Resolved ({resolved.length})</strong>
              <div
                style={{ marginTop: SPACING.labelHeaderGap, maxHeight: SPACING.resolvedListMaxHeight, overflowY: 'auto' }}
              >
                {resolved.map((e, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: SPACING.resolvedItemColumnGap,
                      alignItems: 'center',
                      padding: SPACING.resolvedItemPadding,
                      background: COLORS.resolvedItemBg,
                      marginBottom: SPACING.resolvedItemGap,
                      borderRadius: SPACING.resolvedItemBorderRadius,
                      fontSize: FONT.cardRow,
                    }}
                  >
                    <span style={{ minWidth: SPACING.resolvedQtyMinWidth, textAlign: 'right', color: COLORS.tag }}>
                      {e.quantity}×
                    </span>
                    <span style={{ flexGrow: 1 }}>{e.name}</span>
                    {e.setCode && (
                      <span style={{ color: COLORS.tag }}>{e.setCode.toUpperCase()}</span>
                    )}
                    <span style={{ color: COLORS.tag }}>{e.condition}</span>
                    <span style={{ color: COLORS.tag }}>{e.finish}</span>
                    {e.isSideboard && (
                      <span style={{ color: COLORS.sideboardTag }}>SB</span>
                    )}
                    {e.isCommander && (
                      <span style={{ color: COLORS.commanderTag }}>CMD</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {unresolved.length > 0 && (
              <div>
                <strong style={{ color: COLORS.unresolvedHeading }}>
                  Unresolved ({unresolved.length})
                </strong>
                <p
                  style={{
                    fontSize: FONT.note,
                    color: COLORS.mutedNote,
                    margin: SPACING.unresolvedNotMargin,
                  }}
                >
                  These card names could not be matched. Add them manually after import.
                </p>
                <ul style={{ margin: 0, paddingLeft: SPACING.unresolvedListPaddingLeft }}>
                  {unresolved.map((name, i) => (
                    <li key={i} style={{ fontSize: FONT.cardRow, marginBottom: SPACING.unresolvedItemGap }}>
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === STEP.INPUT && (
          <>
            <Button onClick={handleClose} appearance="subtle">
              Cancel
            </Button>
            <Button
              onClick={handlePreview}
              appearance="primary"
              loading={previewing}
              disabled={previewDisabled}
            >
              Preview
            </Button>
          </>
        )}

        {step === STEP.PREVIEW && (
          <>
            <Button onClick={resetToInput} appearance="subtle">
              Back
            </Button>
            <Button
              onClick={handleImport}
              appearance="primary"
              loading={importing}
              disabled={importing}
            >
              Import {resolved.length} card{resolved.length !== 1 ? 's' : ''}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>

    <CreateCollectionModal
      open={showCreateModal}
      onClose={() => setShowCreateModal(false)}
      onCreated={handleNewCollectionCreated}
      defaultValues={createModalDefaults}
    />
    </>
  );
};

export default ImportDeckModal;
